import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, ExternalLink, Stethoscope } from 'lucide-react';
import api from '../services/apiClient';
import useAuthStore from '../store/authStore';
import { Button, Field, Select } from '../components/ui';
import { toast } from '../components/toast';
import { NETWORK_LABELS, NETWORK_ICONS, NETWORK_COLORS } from '../lib/networks';

export default function Connections() {
  const { currentCompany, reloadCompanies } = useAuthStore();
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState({ configured: false, brands: [] });
  const [loading, setLoading] = useState(true);
  const [blogId, setBlogId] = useState('');
  const [editingBrand, setEditingBrand] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugging, setDebugging] = useState(false);

  const companyId = currentCompany?.id;

  // Normaliza nome para casar empresa <-> marca (ex: "Skinz" == "SKINZ")
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');

  const runDebug = async () => {
    setDebugging(true);
    try {
      setDebugData(await api.get('/metricool/debug'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDebugging(false);
    }
  };

  const load = async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [networks, brandsRes] = await Promise.all([
        api.get(`/companies/${companyId}/networks`),
        api.get('/metricool/brands')
      ]);
      setData(networks);
      setBrands(brandsRes);
      setEditingBrand(false);

      if (networks.metricoolBlogId) {
        setBlogId(networks.metricoolBlogId);
      } else {
        // Auto-sugere a marca cujo nome bate com o da empresa
        const match = (brandsRes.brands || []).find((b) => norm(b.label) === norm(currentCompany.name));
        setBlogId(match ? match.blogId : '');
        if (!match) setEditingBrand(true); // sem sugestao: ja abre o seletor
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const saveBlogId = async () => {
    try {
      await api.put(`/companies/${companyId}/metricool`, { metricoolBlogId: blogId });
      toast.success('Marca vinculada');
      await reloadCompanies();
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!companyId) return <p className="muted">Selecione uma empresa no topo.</p>;

  return (
    <div>
      <div className="page-actions">
        <Button variant="secondary" onClick={load}><RefreshCw size={15} /> Atualizar</Button>
      </div>

      <div className="card mb16">
        {(() => {
          const isLinked = Boolean(data?.metricoolBlogId);
          const linked = (brands.brands || []).find((b) => String(b.blogId) === String(data?.metricoolBlogId));
          const suggested = !isLinked && blogId && (brands.brands || []).some((b) => String(b.blogId) === String(blogId));

          // Estado compacto: ja vinculado e nao esta trocando
          if (isLinked && !editingBrand) {
            return (
              <div className="row between">
                <div>
                  <div className="muted" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }}>Marca do Metricool</div>
                  <div className="row mt8" style={{ gap: 8 }}>
                    <CheckCircle2 size={18} color="var(--success)" />
                    <strong>{linked ? linked.label : 'Marca'} </strong>
                    <span className="muted">#{data.metricoolBlogId}</span>
                  </div>
                </div>
                <div className="row">
                  <Button variant="secondary" className="sm" onClick={() => setEditingBrand(true)}>Trocar marca</Button>
                  <a href="https://app.metricool.com" target="_blank" rel="noreferrer" className="btn secondary sm">
                    <ExternalLink size={14} /> Abrir Metricool
                  </a>
                </div>
              </div>
            );
          }

          // Estado de configuracao: vincular/trocar
          return (
            <>
              <h3 style={{ marginTop: 0 }}>Vincular marca do Metricool</h3>
              <p className="muted">
                Ligue esta empresa do Pulso à marca correspondente no Metricool (sistemas diferentes). Você faz isso <strong>uma vez</strong>;
                o OAuth de cada rede continua sendo feito no painel do Metricool.
              </p>

              {!brands.configured && (
                <p className="badge yellow">Metricool não configurado no servidor (defina METRICOOL_USER_ID e METRICOOL_TOKEN).</p>
              )}

              <div className="row wrap mt8" style={{ alignItems: 'flex-end' }}>
                <div style={{ minWidth: 260 }}>
                  <Field label="Marca (blogId)" hint={suggested ? 'Sugerida automaticamente pelo nome da empresa' : undefined}>
                    {brands.configured && brands.brands.length > 0 ? (
                      <Select value={blogId} onChange={(e) => setBlogId(e.target.value)}>
                        <option value="">— selecione —</option>
                        {brands.brands.map((b) => (
                          <option key={b.blogId} value={b.blogId}>{b.label} (#{b.blogId})</option>
                        ))}
                      </Select>
                    ) : (
                      <input className="input" value={blogId} onChange={(e) => setBlogId(e.target.value)} placeholder="ex: 1234567" />
                    )}
                  </Field>
                </div>
                <Button onClick={saveBlogId} disabled={!blogId}>{suggested ? 'Confirmar marca' : 'Vincular marca'}</Button>
                {isLinked && <Button variant="ghost" onClick={() => { setEditingBrand(false); setBlogId(data.metricoolBlogId); }}>Cancelar</Button>}
                <a href="https://app.metricool.com" target="_blank" rel="noreferrer" className="btn secondary">
                  <ExternalLink size={15} /> Abrir Metricool
                </a>
              </div>
            </>
          );
        })()}
      </div>

      <div className="card">
        <div className="row between mb8">
          <h3 style={{ margin: 0 }}>Status das redes</h3>
          <Button variant="ghost" className="sm" onClick={runDebug} loading={debugging}>
            <Stethoscope size={15} /> Diagnóstico
          </Button>
        </div>

        {loading ? (
          <p className="muted">Carregando…</p>
        ) : (
          <div className="cards-grid">
            {(data?.networks || []).map((n) => {
              const Icon = NETWORK_ICONS[n.network];
              return (
                <div key={n.network} className="net-card">
                  <div className="net-head">
                    <span className="net-name">
                      <Icon size={20} color={NETWORK_COLORS[n.network]} />
                      {NETWORK_LABELS[n.network]}
                    </span>
                    {n.connected
                      ? <span className="badge green"><CheckCircle2 size={13} /> Conectado</span>
                      : <span className="badge gray"><XCircle size={13} /> Desconectado</span>}
                  </div>
                  {n.externalHandle && <span className="net-handle">{n.externalHandle}</span>}
                </div>
              );
            })}
          </div>
        )}

        {data && !data.metricoolConfigured && (
          <p className="hint mt8">Sem Metricool configurado, o status fica em cache local.</p>
        )}

        {debugData && (
          <div className="mt16">
            <p className="hint">Campos retornados pela marca: <code>{(debugData.rawKeys || []).join(', ')}</code></p>
            <pre style={{
              background: '#0f1115', color: '#cdd3dd', padding: 12, borderRadius: 8,
              overflow: 'auto', maxHeight: 320, fontSize: 12
            }}>{JSON.stringify(debugData, null, 2)}</pre>
            <p className="hint">Se o LinkedIn aparecer conectado no JSON mas não no painel acima, me mande este JSON.</p>
          </div>
        )}
      </div>
    </div>
  );
}
